import { useEffect, useRef, useState } from "react";

/**
 * Hook to derive page pagination information from a FileViewer DOM tree.
 * Looks for <li> nodes (optionally with a data-index attr) that represent
 * page breaks, counts them and then determines which page is currently visible
 * using IntersectionObserver.
 */
export const useFileViewerPagination = (
  /**
   * Ref pointing to a DOM element that contains the FileViewer output.
   * This can safely be a wrapper around the FileViewer component since the hook
   * will traverse its subtree looking for page <li> elements.
   */
  containerRef: React.RefObject<HTMLElement>,
  {
    /** percentage of each page that must be visible before we consider it the active one */
    threshold = 0.9,
    /** current zoom level as a percentage (e.g., 100 for 100%) */
    zoomLevel = 100,
  }: { threshold?: number; zoomLevel?: number } = {},
) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const lastScrollPosition = useRef<number>(0);
  const isScrollingForward = useRef<boolean>(true);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // keep the current list of pages in a ref so observers can access it
  const pagesRef = useRef<HTMLElement[]>([]);

  // rescans the DOM for <li> pages and updates totalPages.
  const scanForPages = () => {
    if (!containerRef.current) return;

    // prefer li[data-index] if available, else fallback to any li descendant.
    const newPages = Array.from(
      containerRef.current.querySelectorAll<HTMLElement>("li[data-index], li"),
    );

    //only update if the list length has changed to avoid re-creating observers
    if (newPages.length !== pagesRef.current.length) {
      pagesRef.current = newPages;
      setTotalPages(newPages.length || 1);
    }
  };

  const goToPage = (page: number) => {
    const idx = page - 1;
    const el = pagesRef.current[idx];
    if (el) {
      isScrollingForward.current = page > currentPage;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // first scan + setup MutationObserver so we detect when FileViewer
  // loads additional pages lazily
  useEffect(() => {
    if (!containerRef.current) return;

    scanForPages();

    const mo = new MutationObserver(() => {
      scanForPages();
    });

    mo.observe(containerRef.current, {
      childList: true,
      subtree: true,
    });

    return () => mo.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef]);

  // update current page whenever pages list changes, container ref changes, or zoom level changes
  useEffect(() => {
    if (!containerRef.current || pagesRef.current.length === 0) return;

    // Track scroll direction
    const handleScroll = () => {
      if (!containerRef.current) return;
      const currentScrollPos = containerRef.current.scrollTop;
      isScrollingForward.current =
        currentScrollPos > lastScrollPosition.current;
      lastScrollPosition.current = currentScrollPos;
    };

    containerRef.current.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    // Adjust threshold based on zoom level
    // When zoomed in (>100%), we want to be more lenient with the threshold
    // When zoomed out (<100%), we can be stricter
    const adjustedThreshold = Math.min(
      threshold * (100 / zoomLevel), // Decrease threshold when zoomed in
      0.95, // Cap at 95% to ensure we always require significant visibility
    );

    // Disconnect existing observer if any
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        // Filter entries that meet the adjusted threshold
        const visibleEntries = entries
          .filter(
            (e) => e.isIntersecting && e.intersectionRatio >= adjustedThreshold,
          )
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visibleEntries.length > 0) {
          // Get indices of visible pages
          const visibleIndices = visibleEntries
            .map((entry) =>
              pagesRef.current.findIndex((el) => el === entry.target),
            )
            .filter((idx) => idx !== -1);

          if (visibleIndices.length > 0) {
            // When multiple pages are visible enough, prefer the one in the direction we're moving
            const targetIndex = isScrollingForward.current
              ? Math.min(...visibleIndices) // When scrolling forward, prefer earlier visible page
              : Math.max(...visibleIndices); // When scrolling backward, prefer later visible page

            setCurrentPage(targetIndex + 1);
          }
        }
      },
      {
        root: null, // viewport; using null avoids FileViewer internal scroll quirks
        threshold: adjustedThreshold,
      },
    );

    pagesRef.current.forEach((el) => observerRef.current?.observe(el));

    return () => {
      observerRef.current?.disconnect();
      containerRef.current?.removeEventListener("scroll", handleScroll);
    };
  }, [containerRef, threshold, totalPages, zoomLevel]);

  return { currentPage, totalPages, goToPage } as const;
};
